import React from "react";
import { cx } from "./format";

/** A surface that groups related content. Use sparingly: not everything is a card. */
export const Card = ({ as: Tag = "section", padded = true, className, children, ...rest }) => (
  <Tag
    className={cx("bg-cm-surface border border-cm-border rounded-cm-lg min-w-0", padded && "p-4 sm:p-5", className)}
    {...rest}
  >
    {children}
  </Tag>
);

/** Card heading row: title, optional one-line description, optional actions on the right. */
export const CardHeader = ({ title, description, actions, className }) => (
  <div className={cx("flex flex-wrap items-start justify-between gap-3 mb-4", className)}>
    <div className="min-w-0">
      {title && <h3 className="text-base font-bold text-cm-text m-0">{title}</h3>}
      {description && <p className="text-[13px] text-cm-muted mt-0.5 mb-0">{description}</p>}
    </div>
    {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
  </div>
);

/**
 * Top of every page: what the page is, one plain sentence about what it's for,
 * and at most one primary action (plus secondary ones) on the right.
 */
export const PageHeader = ({ title, description, eyebrow, actions, children, className }) => (
  <header className={cx("flex flex-wrap items-start justify-between gap-4 mb-5", className)}>
    <div className="min-w-0 max-w-3xl">
      {eyebrow && (
        <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-cm-gold-text mb-1.5">{eyebrow}</div>
      )}
      <h1 className="font-display text-2xl sm:text-[26px] font-extrabold text-cm-text m-0 leading-tight">{title}</h1>
      {description && <p className="text-sm text-cm-muted mt-1.5 mb-0">{description}</p>}
      {children}
    </div>
    {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
  </header>
);

/** Page body wrapper: consistent vertical rhythm between sections. */
export const Page = ({ className, children }) => (
  <div className={cx("flex flex-col gap-5 min-w-0", className)}>{children}</div>
);

/** One row of filters that controls everything below it. */
export const FilterBar = ({ children, actions, className }) => (
  <div
    className={cx(
      "flex flex-wrap items-center gap-2 p-3 rounded-cm-lg bg-cm-surface border border-cm-border",
      className,
    )}
  >
    <div className="flex flex-1 flex-wrap items-center gap-2 min-w-0">{children}</div>
    {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
  </div>
);

/** Responsive grid for stat cards: 1 → 2 → 4 columns. */
export const StatGrid = ({ children, cols = 4, className }) => (
  <div
    className={cx(
      "grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2",
      cols >= 4 ? "xl:grid-cols-4" : cols === 3 ? "lg:grid-cols-3" : "",
      className,
    )}
  >
    {children}
  </div>
);

/** Section title inside a page (between groups of cards). */
export const SectionTitle = ({ children, description, actions, className }) => (
  <div className={cx("flex flex-wrap items-end justify-between gap-3", className)}>
    <div>
      <h2 className="text-lg font-bold font-sans text-cm-text m-0">{children}</h2>
      {description && <p className="text-[13px] text-cm-muted mt-0.5 mb-0">{description}</p>}
    </div>
    {actions}
  </div>
);

export const Divider = ({ className }) => <hr className={cx("border-cm-border my-4", className)} />;

/** Label/value pair for detail panels (player profile, transaction detail…). */
export const KeyValue = ({ label, children, className }) => (
  <div className={cx("min-w-0", className)}>
    <dt className="text-xs font-semibold text-cm-muted">{label}</dt>
    <dd className="m-0 mt-0.5 text-sm text-cm-text break-words">{children ?? "—"}</dd>
  </div>
);
